import { FC, useMemo } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  IconButton,
  Box,
  Typography,
  Paper,
  Chip,
  Select,
  MenuItem,
  SelectChangeEvent,
  Alert,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { AllAreasType, AreaData } from '@/utilities/types/table'

const infoBoxStyle = {
  bgcolor: '#F3F4F6',
  border: '1px solid',
  borderColor: 'rgb(226, 232, 240)',
  borderRadius: '4px',
  p: 2,
  width: '100%',
}

interface AssignAreaFormData {
  areaController: string
  userAreas: AreaData[]
}

interface ModalProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: AssignAreaFormData) => void
  formData: AssignAreaFormData
  setFormData: (data: AssignAreaFormData) => void
  areas: AllAreasType[]
  areaControllerName: string
  isLoading?: boolean
  error?: string | null
  existingAreas: AreaData[]
}

export const AssignAreaModal: FC<ModalProps> = ({
  open,
  onClose,
  onSubmit,
  formData,
  setFormData,
  areas = [],
  areaControllerName,
  existingAreas,
  isLoading = false,
  error = null,
}) => {
  const assignedAreaIds = useMemo(
    () =>
      formData.userAreas.map((a) => a.area?.id?.toString() ?? a.id.toString()),
    [formData.userAreas],
  )

  const availableAreas = useMemo(() => {
    return areas.filter((area) => !assignedAreaIds.includes(area.id.toString()))
  }, [areas, assignedAreaIds])

  const handleAdd = (event: SelectChangeEvent<string>) => {
    const areaId = parseInt(event.target.value, 10)
    console.log('Selected area ID from dropdown:', areaId)

    const selectedArea = areas.find((area) => area.id === areaId)
    if (selectedArea) {
      const wrapped: AreaData = {
        id: selectedArea.id,
        area: selectedArea,
        branches: selectedArea.branches || [],
        dateCreated: selectedArea.dateCreated,
        dateModified: selectedArea.dateModified,
        dateDeleted: null,
      }

      console.log('Adding wrapped area:', wrapped)

      setFormData({
        ...formData,
        userAreas: [...formData.userAreas, wrapped],
      })
    }
  }

  const handleRemove = (areaId: number) => {
    console.log('Removing area ID:', areaId)
    setFormData({
      ...formData,
      userAreas: formData.userAreas.filter((a) => a.id !== areaId),
    })
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6">Assign Areas</Typography>
          <IconButton
            onClick={onClose}
            disabled={isLoading}
            sx={{ width: 20, height: 20 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Area Controller
          </Typography>
          <Paper variant="outlined" sx={infoBoxStyle}>
            <Typography textTransform={'uppercase'}>
              {areaControllerName}
            </Typography>
          </Paper>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Assigned Areas
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, minHeight: '80px' }}>
            {formData.userAreas.length === 0 ? (
              <Typography color="text.secondary">No areas assigned</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {formData.userAreas.map((area) => (
                  <Paper key={area.id} variant="outlined" sx={{ p: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Box>
                        <Typography>
                          {area.area.name} - {area.area.code}
                        </Typography>
                        {area.area.description && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            {area.area.description}
                          </Typography>
                        )}
                      </Box>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleRemove(area.id)}
                      >
                        Remove
                      </Button>
                    </Box>
                    {area.area.branches?.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Branches under {area.area.name}:
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                            mt: 0.5,
                          }}
                        >
                          {area.area.branches.map((branch) => (
                            <Chip
                              key={branch.id}
                              label={`${branch.name} - ${branch.dealerName}`}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Paper>
                ))}
              </Box>
            )}
          </Paper>
        </Box>

        <FormControl fullWidth>
          <InputLabel id="add-areas-label">Add Areas</InputLabel>
          <Select
            labelId="add-areas-label"
            value=""
            onChange={handleAdd}
            label="Add Areas"
            disabled={isLoading || availableAreas.length === 0}
          >
            {availableAreas.map((area) => (
              <MenuItem key={area.id} value={area.id.toString()} sx={{ py: 1 }}>
                <Box>
                  <Typography>
                    {area.name} - {area.code}
                  </Typography>
                  {area.description && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      {area.description}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            ))}
            {availableAreas.length === 0 && (
              <MenuItem disabled>No areas available</MenuItem>
            )}
          </Select>
        </FormControl>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            console.log('Submitting form data:', formData)
            console.log(
              'Extracted areaIDs:',
              formData.userAreas.map((a) => a.area?.id ?? a.id),
            )
            onSubmit(formData)
          }}
          disabled={isLoading}
        >
          {isLoading ? 'Submitting...' : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
